import { Capacitor, CapacitorHttp } from "@capacitor/core";
import router from "@/router";
import store from '@/store';

function getPreferences() {
  const raw = localStorage.getItem('preferences');
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function savePreferences(preferences) {
  localStorage.setItem('preferences', JSON.stringify(preferences));
}

function getActiveTokens(preferences) {
  if (!preferences || !preferences.schools) {
    return { access: null, refresh: null };
  }
  for (const school of preferences.schools) {
    if (school?.isActive && school?.users?.length > 0) {
      for (const user of school.users) {
        if (user.isActive && user.auth) {
          return {
            access: user.auth.access,
            refresh: user.auth.refresh
          };
        }
      }
    }
  }
  return { access: null, refresh: null };
}

function updateTokens(preferences, newTokens) {
  if (!preferences || !preferences.schools || !Array.isArray(preferences.schools)) {
    return;
  }
  for (const school of preferences.schools) {
    if (school?.isActive && school?.users?.length > 0) {
      for (const user of school.users) {
        if (user.isActive) {
          user.auth = newTokens;
        }
      }
    }
  }
  savePreferences(preferences);
}

async function handleServerError(status) {
  if (status >= 500) {
    if (router.currentRoute?.value?.name !== 'server-error') {
      await router.replace({ name: 'server-error' });
    }
  }
}

async function handleNetworkError(method) {
  const isOnline = store.state.network.isOnline;
  const currentRoute = router.currentRoute.value;

  if (!isOnline) {
    // await router.replace({
    //   name: 'offline',
    //   query: { redirect: currentRoute.fullPath }
    // });
    store.commit('network/SET_IS_REQUEST_FAILED', true)
  }
}

async function logout() {
  const preferences = getPreferences();
  if (!preferences) {
    localStorage.removeItem('preferences');
    await router.replace({ name: 'home' });
    window.location.reload();
    return
  }

  let activeSchoolIndex = preferences.schools.findIndex(s => s.isActive);
  let activeSchool = preferences.schools[activeSchoolIndex];
  let activeUserIndex = activeSchool?.users?.findIndex(u => u.isActive);

  if (preferences.schools.length < 2) {
    if (activeSchool.users.length < 2) {
      localStorage.removeItem('preferences');
    } else {
      activeSchool.users.splice(activeUserIndex, 1);
      activeSchool.users[0].isActive = true;
      savePreferences(preferences);
    }
  } else {
    if (activeSchool.users.length < 2) {
      preferences.schools.splice(activeSchoolIndex, 1);
      preferences.schools[0].isActive = true;
      preferences.schools[0].users[0].isActive = true;
    } else {
      activeSchool.users.splice(activeUserIndex, 1);
      activeSchool.users[0].isActive = true;
    }
    savePreferences(preferences);
  }

  await router.replace({ name: 'home' });
  window.location.reload();
}

function normalizeParams(params) {
  if (!params) return params;
  const result = {};
  for (const key in params) {
    if (Array.isArray(params[key])) {
      result[key] = params[key].map(v => String(v));
    } else if (params[key] !== null && params[key] !== undefined) {
      result[key] = String(params[key]);
    }
  }
  return result;
}

async function httpRequest(options) {
  const preferences = getPreferences();
  let tokens = { access: null, refresh: null };

  if (options.authorized) {
    if (!preferences) return logout();
    tokens = getActiveTokens(preferences);

    if (tokens.access) {
      options.options.headers = {
        Authorization: `Bearer ${tokens.access}`,
        'Content-Type': 'application/json'
      };
    }
  } else {
    options.options.headers = options.options.headers || {
      'Content-Type': 'application/json'
    };
  }

  // cek semua params bentuk string di IOS
  if (Capacitor.getPlatform() === "ios" && options.options.params) {
    options.options.params = normalizeParams(options.options.params);
  }

  // cek body data bentuk string di IOS
  if (
    Capacitor.getPlatform() === "ios" &&
    options.options.data &&
    !(options.options.data instanceof FormData) &&
    typeof options.options.data !== "string"
  ) {
    if (typeof options.options.data === "object") {
      options.options.data = JSON.stringify(options.options.data);
    } else {
      options.options.data = String(options.options.data);
    }
  }

  const makeRequest = async () => {
    try {
      if (options.options.data instanceof FormData) {
        const res = await fetch(options.options.url, {
          method: options.options.method,
          body: options.options.data,
          headers: tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {}
        });
        res.data = await res.json()
        return res
      } else if (options.isDownload) {
        const res = await fetch(options.options.url, {
          method: options.options.method,
          headers: tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {}
        });
        const arrayBuffer = await res.arrayBuffer();
        return {
          status: res.status,
          data: arrayBuffer
        };
      } else {
        return await CapacitorHttp.request(options.options);
      }
    } catch (error) {
      const platform = Capacitor.getPlatform();
      const isOffline =
        !store.state.network.isOnline || // dari listener
        (platform === "ios" &&
          (error.code === "NSURLErrorDomain" ||
            error.errorMessage?.includes("offline"))) ||
        (platform === "android" &&
          error.code === "UnknownHostException");

      // Tangani error network/offline
      if (isOffline) {
        await handleNetworkError(options.options.method);
        return null;
      }

      throw error; // kalau error lain, lempar lagi
    }
  };

  let res = await makeRequest();
  if (!res) return null;
  await handleServerError(res.status);

  // Refresh token klo expired
  if ((res.status === 403 && res.statusText === 'Forbidden') ||
    (res.data?.code === 'token_not_valid')) {

    const refreshResponse = await CapacitorHttp.request({
      url: `${options.options.url.split('/api')[0]}/api/token/refresh/`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: JSON.stringify({ refresh: tokens.refresh })
    });

    if (refreshResponse.status === 200) {
      // Perbarui token
      updateTokens(preferences, refreshResponse.data);
      tokens.access = refreshResponse.data.access;

      options.options.headers = {
        Authorization: `Bearer ${tokens.access}`,
        'Content-Type': 'application/json'
      };

      res = await makeRequest();
    } else {
      return logout();
    }
  }

  return res;
}

export default httpRequest;
